import { required } from "ra-core";
import { Separator } from "@/components/ui/separator";
import { ArrayInput } from "@/components/admin/array-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextInput } from "@/components/admin/text-input";

import type { Sale } from "../types";
import {
  auctionStatuses,
  auctionTypes,
  contactPointTypes,
  listingStatuses,
  occupancyTypes,
  prospectStages,
} from "./prospectModel";

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-4">
    <h6 className="text-lg font-semibold">{title}</h6>
    {children}
  </div>
);

export const ProspectInputs = () => (
  <div className="flex flex-col gap-2 p-1">
    <div className="flex gap-10 md:gap-6 flex-col md:flex-row">
      <div className="flex flex-col gap-10 flex-1">
        <OwnerInputs />
        <PropertyInputs />
      </div>
      <Separator orientation="vertical" className="hidden md:block shrink-0" />
      <div className="flex flex-col gap-10 flex-1">
        <ValuationInputs />
        <ListingInputs />
        <PreAuctionInputs />
        <PipelineInputs />
      </div>
    </div>
  </div>
);

const OwnerInputs = () => (
  <Section title="Owner">
    <TextInput source="owner_name" validate={required()} helperText={false} />
    <TextInput source="co_owner_name" helperText={false} />
    <ArrayInput source="phones" helperText={false}>
      <SimpleFormIterator inline disableReordering>
        <TextInput source="number" label="Phone" helperText={false} />
        <SelectInput
          source="type"
          label={false}
          choices={contactPointTypes}
          defaultValue="Work"
          helperText={false}
          className="w-28 min-w-28"
        />
      </SimpleFormIterator>
    </ArrayInput>
    <ArrayInput source="emails" helperText={false}>
      <SimpleFormIterator inline disableReordering>
        <TextInput source="email" label="Email" helperText={false} />
        <SelectInput
          source="type"
          label={false}
          choices={contactPointTypes}
          defaultValue="Work"
          helperText={false}
          className="w-28 min-w-28"
        />
      </SimpleFormIterator>
    </ArrayInput>
    <SelectInput
      source="occupancy"
      choices={occupancyTypes}
      helperText={false}
    />
    <DateInput source="owner_birthday" helperText={false} />
    <TextInput source="mailing_address" helperText={false} />
    <div className="flex gap-2">
      <TextInput source="mailing_city" helperText={false} />
      <TextInput source="mailing_state" helperText={false} />
      <TextInput source="mailing_zip" helperText={false} />
    </div>
  </Section>
);

const PropertyInputs = () => (
  <Section title="Property">
    <TextInput source="property_address" helperText={false} />
    <div className="flex gap-2">
      <TextInput source="property_city" helperText={false} />
      <TextInput source="property_state" helperText={false} />
      <TextInput source="property_zip" helperText={false} />
    </div>
    <TextInput source="county" helperText={false} />
    <TextInput source="subdivision" helperText={false} />
    <TextInput source="parcel_apn" label="Parcel APN" helperText={false} />
    <TextInput source="legal_description" multiline helperText={false} />
    <TextInput source="property_type" helperText={false} />
    <div className="flex gap-2">
      <NumberInput source="beds" helperText={false} />
      <NumberInput source="baths" step={0.5} helperText={false} />
      <NumberInput source="sqft" helperText={false} />
    </div>
    <div className="flex gap-2">
      <NumberInput source="lot_acres" step={0.01} helperText={false} />
      <NumberInput source="stories" helperText={false} />
      <NumberInput source="year_built" helperText={false} />
    </div>
  </Section>
);

const ValuationInputs = () => (
  <Section title="Valuation">
    <div className="flex gap-2">
      <NumberInput source="tax_value" helperText={false} />
      <NumberInput source="tax_amount" helperText={false} />
      <NumberInput source="tax_year" helperText={false} />
    </div>
    <NumberInput source="avm" label="AVM" helperText={false} />
    <NumberInput source="estimated_equity" helperText={false} />
    <NumberInput source="original_mortgage" helperText={false} />
    <NumberInput
      source="length_of_ownership_yrs"
      step={0.1}
      helperText={false}
    />
    <div className="flex gap-2">
      <NumberInput source="sold_amount" helperText={false} />
      <DateInput source="sold_date" helperText={false} />
    </div>
  </Section>
);

const ListingInputs = () => (
  <Section title="Listing">
    <SelectInput
      source="listing_status"
      choices={listingStatuses}
      helperText={false}
    />
    <div className="flex gap-2">
      <NumberInput source="list_price" helperText={false} />
      <DateInput source="list_date" helperText={false} />
    </div>
    <NumberInput source="days_on_market" helperText={false} />
    <TextInput source="mls_id" label="MLS ID" helperText={false} />
    <TextInput source="list_agent" helperText={false} />
    <TextInput source="list_office" helperText={false} />
  </Section>
);

const PreAuctionInputs = () => (
  <Section title="Pre-Auction">
    <DateInput source="auction_date" helperText={false} />
    <SelectInput source="auction_type" choices={auctionTypes} helperText={false} />
    <SelectInput
      source="auction_status"
      choices={auctionStatuses}
      helperText={false}
    />
    <div className="flex gap-2">
      <NumberInput source="opening_bid" helperText={false} />
      <NumberInput source="min_bid" helperText={false} />
    </div>
    <div className="flex gap-2">
      <NumberInput source="lien_amount" helperText={false} />
      <NumberInput source="lien_position" helperText={false} />
    </div>
    <NumberInput source="judgment_amount" helperText={false} />
    <TextInput source="plaintiff_lender" helperText={false} />
    <TextInput source="case_number" helperText={false} />
    <TextInput source="trustee" helperText={false} />
    <TextInput source="sale_location" helperText={false} />
    <DateInput source="redemption_deadline" helperText={false} />
  </Section>
);

const PipelineInputs = () => (
  <Section title="Pipeline">
    <SelectInput
      source="stage"
      choices={prospectStages}
      defaultValue="Researching"
      validate={required()}
      helperText={false}
    />
    <TextInput source="source" helperText={false} />
    <ReferenceInput
      reference="sales"
      source="sales_id"
      sort={{ field: "last_name", order: "ASC" }}
      filter={{ "disabled@neq": true }}
    >
      <SelectInput
        helperText={false}
        optionText={(choice: Sale) =>
          `${choice.first_name} ${choice.last_name}`
        }
        validate={required()}
      />
    </ReferenceInput>
  </Section>
);
